import { z } from 'zod';

const itineraryAgentContext = z.object({
  contextType: z.literal('itinerary'),
  itineraryId: z.uuid(),
  dayId: z.uuid().nullish(),
});

export const suggestInput = z.discriminatedUnion('contextType', [
  itineraryAgentContext.extend({
    q: z.string().max(200).default(''),
    limit: z.number().int().min(1).max(40).default(8),
  }),
]);

// Customer-quote shape reused across day intents + inferred preferences.
// The field on `agentSuggestion` was retired in pen 2026-05-22 (Day picks
// card no longer surfaces a quote); kept here because other domains still
// reference the same shape.
export const conversationQuote = z.object({
  quote: z.string(),
  sourceLabel: z.string(),
});

export const agentSuggestion = z.object({
  id: z.uuid(),
  title: z.string(),
  subtitle: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  priceCents: z.number().int().nullable(),
  currency: z.string().length(3).nullable(),
  imageUrl: z.url().nullable(),
  durationMinutes: z.number().int().nullable(),
  matchScore: z.number(),
  matchedHashtags: z.array(z.string()),
  whyMatched: z.array(z.string()),
  historyUses: z.number().int(),
});

export const suggestDegradedReason = z.enum(['llm-unavailable']);

export const suggestOutput = z.object({
  bestMatches: z.array(agentSuggestion),
  pairings: z.array(agentSuggestion),
  interpretation: z
    .object({
      intent: z.string(),
      entities: z.array(
        z.object({
          type: z.enum(['hashtag', 'place', 'time', 'guests', 'budget']),
          value: z.string(),
        })
      ),
      matchedCount: z.number().int(),
      // Raw hashtag ranking from the LLM, surfaced for transparency / debug.
      // Includes hashtags the LLM matched even when no product carries them.
      rankedHashtags: z
        .array(
          z.object({
            id: z.uuid(),
            name: z.string(),
            category: z.string(),
            relevance: z.number(),
            why: z.string(),
          })
        )
        .optional(),
    })
    .nullable(),
  degraded: suggestDegradedReason.nullable().optional(),
});

export const createItineraryItemInput = z.object({
  itineraryVersionId: z.uuid(),
  dayIndex: z.number().int().min(0),
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable(),
  productId: z.uuid(),
  /** When true, row is created with `is_optional` (builder optionals write-back). */
  isOptional: z.boolean().optional(),
  /**
   * The `startTime` is a fallback, not a time anyone chose (a planner drop
   * sends the product's leg time or 09:00). A dynamic-pricing product's
   * operation times then set the item's start and end (XT-951).
   */
  startTimeIsDefault: z.boolean().optional(),
});

export const transcribeInput = z.object({
  audioBase64: z.string().min(1).max(8_000_000),
  mimeType: z.string().regex(/^audio\/(webm|mp4|wav|mpeg|x-m4a)(;.*)?$/),
  durationMs: z.number().int().min(250).max(30_000),
});

export const transcribeOutput = z.object({
  transcript: z.string(),
  language: z.string().nullable(),
  durationMs: z.number().int(),
});

export const parseEmailExtracted = z.object({
  travellersTotal: z.number().int().nullable(),
  travellersBreakdown: z.array(
    z.object({
      ageBand: z.enum(['baby', 'child', 'teen', 'adult', 'senior']),
      count: z.number().int(),
    })
  ),
  destinations: z.array(
    z.object({
      city: z.string().nullable(),
      country: z.string().nullable(),
      nights: z.number().int().nullable(),
    })
  ),
  startDate: z.iso.date().nullable(),
  endDate: z.iso.date().nullable(),
  interests: z.array(z.string()),
  accommodationStyle: z.enum(['hotel', 'apartment', 'mixed', 'unknown']),
  budgetLevel: z.enum(['economy', 'medium', 'luxury']).nullable(),
  budgetTotal: z
    .object({
      amount: z.number(),
      currency: z.string().length(3),
    })
    .nullable(),
  hasChildren: z.boolean(),
  hasMobilityConcerns: z.boolean(),
  assumptions: z.array(z.string()),
});

// `field` is a free-form hint about which brief field a clarifying question
// targets. Was originally a strict enum, but the LLM legitimately produces
// questions about `endDate`, `interests`, `budgetTotal`, etc. and a parse
// failure here used to discard the whole brief — including the perfectly
// good `extracted` payload. Since no UI surface routes on `field` today
// (the value is rendered as a chip label / debugging aid only), accept
// any string and let downstream code coerce when it ever needs to.
export const parseEmailClarifyingQuestion = z.object({
  field: z.string(),
  question: z.string(),
  type: z.enum(['text', 'choice']),
  options: z.array(z.string()).optional(),
});

export const parseEmailOutput = z.object({
  extracted: parseEmailExtracted,
  clarifyingQuestions: z.array(parseEmailClarifyingQuestion),
});

export const categoryOptionsConversationMessage = z.object({
  role: z.enum(['pasted-customer', 'drafted-by-consultant']),
  body: z.string(),
  sourceIndex: z.number().int(),
  sourceLabel: z.string(),
});

export const categoryOptionsConversationContext = z.object({
  messages: z.array(categoryOptionsConversationMessage),
});

export const categoryOptionsInput = z.object({
  contextType: z.literal('itinerary'),
  itineraryId: z.uuid(),
  extracted: parseEmailExtracted,
  conversationContext: categoryOptionsConversationContext.optional(),
  // Pre-identified taxonomy hashtag IDs from `inboxThreads.inferHashtags`
  // (LLM-driven semantic match between brief + email and the org taxonomy).
  // When supplied, the matcher skips its own internal `rankHashtags` LLM
  // call per destination/bucket and feeds these IDs straight into the
  // product join. This is the architectural unification: one LLM call per
  // thread open instead of N×4. Optional for backwards-compat with v1
  // call sites (sandbox / non-thread entries that lack a thread brief).
  briefHashtagIds: z.array(z.uuid()).optional(),
});

export const categoryOptionsOutput = z.object({
  extracted: parseEmailExtracted,
  options: z.object({
    activities: z.array(agentSuggestion),
    accommodation: z.array(agentSuggestion),
    dining: z.array(agentSuggestion),
    transportation: z.array(agentSuggestion),
  }),
  preselected: z.object({
    activityIds: z.array(z.uuid()),
    accommodationId: z.uuid().nullable(),
    diningIds: z.array(z.uuid()),
    transportationIds: z.array(z.uuid()),
  }),
  degraded: suggestDegradedReason.nullable(),
});

export const buildFromSelectionsSlot = z.enum([
  'morning',
  'lunch',
  'afternoon',
  'dinner',
]);

// Severity model — see PRD §4.1 FR-2.
//   critical   = gap or hard mismatch (red alert)
//   suggestion = soft fit / room to improve (info alert)
//   covered    = client request that IS satisfied (success — engine "heard" you)
export const fitAlertSeverity = z.enum(['critical', 'suggestion', 'covered']);
export const fitAlertCategory = z.enum([
  'activities',
  'accommodation',
  'dining',
  'transportation',
]);

export const fitAlert = z.object({
  id: z.string(),
  severity: fitAlertSeverity,
  category: fitAlertCategory,
  title: z.string(),
  description: z.string(),
  requestedChip: z.string(),
  foundChip: z.string(),
  suggestedHashtags: z.array(z.string()),
  affectedDayIndices: z.array(z.number().int()),
  fixActionLabel: z.string(),
});

// Brief gaps — surfaced in the Conversation panel + Day-picks dock (v3).
// Severity: blocker = should resolve before picking; warning = affects picks;
// suggestion = nice-to-have. `category` drives the coloured pill + icon on the
// BriefGapCard; `affects` is the short "what it impacts" line under the title.
export const briefGapSeverity = z.enum(['blocker', 'warning', 'suggestion']);
export const briefGapAction = z.enum([
  'ask-customer',
  'fill-manually',
  'generate-anyway',
]);
export const briefGapCategory = z.enum([
  'brief',
  'transportation',
  'accommodation',
  'activities',
  'dining',
]);

export const briefGap = z.object({
  id: z.string(),
  severity: briefGapSeverity,
  title: z.string(),
  description: z.string(),
  actions: z.array(briefGapAction),
  // Optional v3 additions — back-compat: older consumers ignore these.
  category: briefGapCategory.optional(),
  affects: z.string().optional(),
});

// Per-day category coverage — drives CategoryTabBar pills + CategoryProgressBar
// segments on Step 2 and the send-gap banner on Step 3 (PRD §4.1 FR-4).
//   complete = enough picks for the day
//   partial  = some picks but below the expected count
//   missing  = no picks
export const dayCategoryCompletion = z.enum(['complete', 'partial', 'missing']);

export const dayCategoryCounts = z.object({
  transport: z.number().int().min(0),
  accommodation: z.number().int().min(0),
  activities: z.number().int().min(0),
  dining: z.number().int().min(0),
});

export const dayCategoryStatus = z.object({
  dayIndex: z.number().int().min(0),
  transport: dayCategoryCompletion,
  accommodation: dayCategoryCompletion,
  activities: dayCategoryCompletion,
  dining: dayCategoryCompletion,
  pickedCounts: dayCategoryCounts,
  requiredCounts: dayCategoryCounts,
});

export const dayIntent = z.object({
  dayIndex: z.number().int(),
  status: z.enum(['ai-picked', 'locked-by-you', 'needs-review']),
  inferredThemes: z.array(z.string()),
  city: z.string().nullable(),
  supportingQuotes: z.array(conversationQuote),
  confidence: z.enum(['high', 'medium', 'low']),
  fitScore: z.number().int().min(0).max(100),
  conflicts: z.number().int().min(0),
  activityCount: z.number().int().min(0),
  description: z.string(),
});

export const preferenceEmphasis = z.enum(['low', 'normal', 'high']);

export const inferredPreference = z.object({
  id: z.string(),
  label: z.string(),
  icon: z.string(),
  category: z.enum([
    'activity',
    'accommodation',
    'dining',
    'logistics',
    'other',
  ]),
  confidence: z.number().min(0).max(100),
  supportingQuote: conversationQuote,
  emphasis: preferenceEmphasis,
  coverageState: z.enum(['covered', 'partial', 'uncovered']),
});

// Screen C — Per-day weight tuning (f32p9)
export const daySliderValues = z.object({
  dayIndex: z.number().int(),
  pace: z.number().min(0).max(10),
  culturalDepth: z.number().min(0).max(10),
  adventureLevel: z.number().min(0).max(10),
  foodFocus: z.number().min(0).max(10),
  budgetSensitivity: z.number().min(0).max(10),
  activeHashtags: z.array(z.string()),
});

// Screen E — Coverage & confidence map (bbzXe)
export const confidenceMatrixEntry = z.object({
  dayIndex: z.number().int(),
  dayLabel: z.string(),
  dateLabel: z.string(),
  stay: z.number().int().min(0).max(100),
  food: z.number().int().min(0).max(100),
  activity: z.number().int().min(0).max(100),
  transfer: z.number().int().min(0).max(100),
});

export const lowConfidenceReason = z.object({
  rank: z.number().int().min(1),
  dayLabel: z.string(),
  category: z.string(),
  explanation: z.string(),
  ctaLabel: z.string(),
  ctaAction: z.enum(['browse-alternatives', 'split-days', 'ask-customer']),
});

export const fitAnalysisResult = z.object({
  alerts: z.array(fitAlert),
  gaps: z.array(briefGap),
  dayIntents: z.array(dayIntent),
  categoryStatus: z.array(dayCategoryStatus),
  inferredPreferences: z.array(inferredPreference),
  confidenceMatrix: z.array(confidenceMatrixEntry),
  lowConfidenceReasons: z.array(lowConfidenceReason),
  runId: z.string(),
  computedAt: z.string(),
  emailsCount: z.number().int().min(0),
  chatMessagesCount: z.number().int().min(0),
  overallConfidence: z.number().int().min(0).max(100),
});

export const recommendationAnalysisInput = z.object({
  itineraryId: z.uuid(),
  extracted: parseEmailExtracted,
  options: categoryOptionsOutput.shape.options,
  preselected: categoryOptionsOutput.shape.preselected,
  conversationContext: categoryOptionsConversationContext.optional(),
});

// The four category-weight levers on the RecTuningPanel (pen `TeHTQ`
// `KZN48`). One lever per product category — canonical order everywhere is
// stay → activities → food → transportation.
export const recommendationCategoryWeights = z.object({
  stay: preferenceEmphasis,
  activities: preferenceEmphasis,
  food: preferenceEmphasis,
  transportation: preferenceEmphasis,
});

export const setRecommendationWeightsInput = z.object({
  itineraryId: z.uuid(),
  options: categoryOptionsOutput.shape.options,
  categoryWeights: recommendationCategoryWeights,
  preferenceEmphasis: z.record(z.string(), preferenceEmphasis),
  dismissedPreferences: z.array(z.string()),
});

export const setRecommendationWeightsOutput = z.object({
  options: categoryOptionsOutput.shape.options,
});

export const resuggestForDayInput = z.object({
  itineraryId: z.uuid(),
  dayIndex: z.number().int().min(0),
  intent: z.string().min(1).max(500),
  currentSelections: z.object({
    activityIds: z.array(z.uuid()),
    accommodationId: z.uuid().nullable(),
    diningIds: z.array(z.uuid()),
    transportationIds: z.array(z.uuid()),
  }),
  conversationContext: categoryOptionsConversationContext.optional(),
});

export const buildFromSelectionsInput = z.object({
  contextType: z.literal('itinerary'),
  itineraryId: z.uuid(),
  extracted: parseEmailExtracted,
  selections: z.object({
    activityIds: z.array(z.uuid()),
    accommodationId: z.uuid().nullable(),
    diningIds: z.array(z.uuid()),
    transportationIds: z.array(z.uuid()),
  }),
  dayOverrides: z
    .record(
      z.string().regex(/^\d+$/),
      z.object({
        activityIds: z.array(z.uuid()).optional(),
        accommodationId: z.uuid().nullable().optional(),
        diningIds: z.array(z.uuid()).optional(),
        transportationIds: z.array(z.uuid()).optional(),
      })
    )
    .optional(),
});

export const buildFromSelectionsScheduleSlot = z.object({
  slot: buildFromSelectionsSlot,
  productId: z.uuid(),
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  contentCategory: z.string(),
});

export const buildFromSelectionsScheduleDay = z.object({
  dayIndex: z.number().int().min(0),
  slots: z.array(buildFromSelectionsScheduleSlot),
});

export const buildFromSelectionsOutput = z.object({
  versionId: z.uuid(),
  durationDays: z.number().int().min(1),
  schedule: z.array(buildFromSelectionsScheduleDay),
  estimatedTotal: z
    .object({
      amount: z.number(),
      currency: z.string().length(3),
    })
    .nullable(),
});

export type SuggestInput = z.infer<typeof suggestInput>;
export type SuggestOutput = z.infer<typeof suggestOutput>;
export type SuggestDegradedReason = z.infer<typeof suggestDegradedReason>;
export type AgentSuggestion = z.infer<typeof agentSuggestion>;
export type CreateItineraryItemInput = z.infer<typeof createItineraryItemInput>;
export type TranscribeInput = z.infer<typeof transcribeInput>;
export type TranscribeOutput = z.infer<typeof transcribeOutput>;
export type ParseEmailOutput = z.infer<typeof parseEmailOutput>;
export type ParseEmailExtracted = z.infer<typeof parseEmailExtracted>;
export type ParseEmailClarifyingQuestion = z.infer<
  typeof parseEmailClarifyingQuestion
>;
export type ConversationQuote = z.infer<typeof conversationQuote>;
export type CategoryOptionsConversationMessage = z.infer<
  typeof categoryOptionsConversationMessage
>;
export type CategoryOptionsConversationContext = z.infer<
  typeof categoryOptionsConversationContext
>;
export type CategoryOptionsInput = z.infer<typeof categoryOptionsInput>;
export type CategoryOptionsOutput = z.infer<typeof categoryOptionsOutput>;
export type BuildFromSelectionsSlot = z.infer<typeof buildFromSelectionsSlot>;
export type BuildFromSelectionsInput = z.infer<typeof buildFromSelectionsInput>;
export type BuildFromSelectionsScheduleSlot = z.infer<
  typeof buildFromSelectionsScheduleSlot
>;
export type BuildFromSelectionsScheduleDay = z.infer<
  typeof buildFromSelectionsScheduleDay
>;
export type BuildFromSelectionsOutput = z.infer<
  typeof buildFromSelectionsOutput
>;
export type FitAlertSeverity = z.infer<typeof fitAlertSeverity>;
export type FitAlertCategory = z.infer<typeof fitAlertCategory>;
export type FitAlert = z.infer<typeof fitAlert>;
export type BriefGapSeverity = z.infer<typeof briefGapSeverity>;
export type BriefGapAction = z.infer<typeof briefGapAction>;
export type BriefGapCategory = z.infer<typeof briefGapCategory>;
export type BriefGap = z.infer<typeof briefGap>;
export type DayCategoryCompletion = z.infer<typeof dayCategoryCompletion>;
export type DayCategoryCounts = z.infer<typeof dayCategoryCounts>;
export type DayCategoryStatus = z.infer<typeof dayCategoryStatus>;
export type DayIntent = z.infer<typeof dayIntent>;
export type PreferenceEmphasis = z.infer<typeof preferenceEmphasis>;
export type InferredPreference = z.infer<typeof inferredPreference>;
export type DaySliderValues = z.infer<typeof daySliderValues>;
export type ConfidenceMatrixEntry = z.infer<typeof confidenceMatrixEntry>;
export type LowConfidenceReason = z.infer<typeof lowConfidenceReason>;
export type FitAnalysisResult = z.infer<typeof fitAnalysisResult>;
export type RecommendationAnalysisInput = z.infer<
  typeof recommendationAnalysisInput
>;
export type ResuggestForDayInput = z.infer<typeof resuggestForDayInput>;
export type RecommendationCategoryWeights = z.infer<
  typeof recommendationCategoryWeights
>;
export type SetRecommendationWeightsInput = z.infer<
  typeof setRecommendationWeightsInput
>;
export type SetRecommendationWeightsOutput = z.infer<
  typeof setRecommendationWeightsOutput
>;
